import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, startWith } from 'rxjs';
import { Calendar } from 'src/app/models/centrex/calendar';
import { RestrictionTypes } from 'src/app/models/centrex/enums/restriction-types';
import { Extension } from 'src/app/models/centrex/extension';
import { Restriction } from 'src/app/models/centrex/restriction';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { RestrictionService } from 'src/app/services/centrex/restriction.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-restriction-form',
    templateUrl: './restriction-form.component.html',
    styleUrls: ['./restriction-form.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatCheckbox,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        MatSelect,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class RestrictionFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    restrictionInformations: Restriction;
    restrictionForm: UntypedFormGroup;
    isCreation: boolean = true;
    extensions: Extension[] = [];
    calendars: Calendar[] = [];

    restrictionTypes = Object.keys(RestrictionTypes);

    selectedExtension: Extension;
    extensionsOptions: Observable<Extension[]>;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private restrictionService: RestrictionService,
        private extensionService: ExtensionService,
        private calendarService: CalendarService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.createForm({} as Restriction);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/restrictions']);
            }
        });
    }

    ngOnInit(): void {
        let restrictionId: string;

        // Récupère l'identifiant de la restriction
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            restrictionId = params['id'];
        });

        this.loadDatas(restrictionId);
    }

    loadDatas(restrictionId: string) {
        this.spinner.show();

        if (restrictionId) {
            this.isCreation = false;

            forkJoin([this.restrictionService.getRestriction(restrictionId), this.extensionService.getExtensions(), this.calendarService.getCalendars()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([restriction, extensions, calendars]) => {
                        this.restrictionInformations = restriction;
                        this.extensions = extensions;
                        this.calendars = calendars;

                        this.selectedExtension = extensions.find((a) => a.id === restriction.extensionId);

                        // Créé le formulaire
                        this.createForm(this.restrictionInformations);
                    },
                    error: () => {
                        this.createForm({} as Restriction);
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.restrictionInformations = new Restriction();
            this.restrictionInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';
            this.restrictionInformations.enabled = true;

            forkJoin([this.extensionService.getExtensions(), this.calendarService.getCalendars()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([extensions, calendars]) => {
                        this.extensions = extensions;
                        this.calendars = calendars;

                        // Créé le formulaire
                        this.createForm(this.restrictionInformations);
                    },
                    error: () => {
                        this.createForm(this.restrictionInformations);
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm(restriction: Restriction) {
        this.restrictionForm = this.formbuilder.group({
            id: [restriction.id],
            organizationId: [restriction.organizationId],
            extensionId: [restriction.extensionId],
            calendarId: [restriction.calendarId],
            name: [restriction.name],
            type: [restriction.type],
            enabled: [restriction.enabled],
            expression: [restriction.expression],
            extension: [{ value: this.selectedExtension, disabled: this.isCreation ? false : true }, [this.autocompleteExtensionValidator()]],
        });

        this.extensionsOptions = this.restrictionForm.get('extension').valueChanges.pipe(
            startWith<string | Extension>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : this.extensionDisplayPipe.transform(value);
                return name ? this.extensionSearch(name as string) : this.extensions.slice();
            })
        );

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.restrictionService
                .addRestriction(this.restrictionInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (restrictionCreated) => {
                        this.toastr.success(this.translate.instant('Restriction.SuccessMessageCreateRestriction'));

                        // Redirige sur la page des restrictions
                        this.router.navigate(['/restrictions']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Restriction.ErrorMessageCreateRestriction'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.restrictionService
                .updateRestriction(this.restrictionInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Restriction.SuccessMessageUpdateRestriction'));

                        this.router.navigate(['/restrictions']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Restriction.ErrorMessageUpdateRestriction'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.restrictionInformations.extensionId = this.restrictionForm.value.extensionId;
        this.restrictionInformations.calendarId = this.restrictionForm.value.calendarId;
        this.restrictionInformations.name = this.restrictionForm.value.name;
        this.restrictionInformations.enabled = this.restrictionForm.value.enabled;
        this.restrictionInformations.type = this.restrictionForm.value.type;
        this.restrictionInformations.expression = this.restrictionForm.value.expression;
    }

    displayExtension = (extension: Extension): string => {
        return extension ? this.extensionDisplayPipe.transform(extension) : '';
    };

    extensionsChange(event: MatAutocompleteSelectedEvent) {
        let extension = event.option.value as Extension;
        if (extension) {
            this.restrictionForm.get('extensionId').setValue(extension.id);
            this.selectedExtension = extension;
        }
    }

    private extensionSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.extensions.filter((a) => this.extensionDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private autocompleteExtensionValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.extensions.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
